import type { Profile, Structure } from "../types/entities";
import type { PropertyRecord } from "../types/portfolio";
import type { BudgetCategory, BudgetSettings, BudgetGroup, BudgetBucket, IncomeRow } from "../types/budget";

export const profiles: Profile[] = [
  {
    id: "alex",
    name: "Alex Chen",
    initials: "AC",
    role: "Admin",
    age: 32,
    gender: "Male",
    annualPreTaxPay: 120000,
    monthlyNetContributionCapacity: 2800,
    marginalTaxRate: 32.5,
    strategyTone: "Balanced",
    reducedIncomePeriods: [],
  },
];

// B106 — Personal-name properties have no structure (structureId === "").
// The legacy "personal" structure entry has been removed from the seed.
// Kellyville's structureId below is now "" (empty = personal-name).
export const structures: Structure[] = [
  {
    id: "chen-trust",
    entityType: "Trust",
    displayName: "Chen Family Trust",
    trusteeName: "Chen Trustee Pty Ltd",
    trusteeType: "Corporate Trustee",
    companyName: "Chen Trustee Pty Ltd",
    trustName: "Chen Family Trust",
    trustType: "Discretionary",
    abn: "",
    tfn: "",
  },
];

export const properties: PropertyRecord[] = [
  {
    id: "kellyville",
    nickname: "Kellyville",
    address: "42 Windermere Drive, Kellyville NSW",
    suburb: "Kellyville",
    state: "NSW",
    postcode: "2155",
    assetClass: "Residential",
    propertyType: "House",
    purchaseDate: "2021-05-14",
    purchasePrice: 890000,
    buyCosts: 35600,
    legalBuyCosts: 2500,
    currentValue: 1060000,
    weeklyRent: 650,
    loanBalance: 714000,
    structureId: "",
    owner: "Alex Chen",
    lender: "NAB",
    loanAccountNumber: "NAB-7831",
    offsetAccount: "NAB-OS-882",
    loanStartDate: "2021-05-12",
    loanTermYears: 30,
    ioTermYears: 0,
    rate: 6.19,
    repaymentType: "P&I",
    rateType: "Variable",
    monthlyExpenses: 950,
    annualRates: 2300,
    annualInsurance: 1650,
    annualStrata: 0,
    annualLandTax: 1400,
    annualMaintenance: 1200,
    annualPropertyManagement: 2704,
    personalTopUp: 0,
    estimatedSellingCostsPct: 2.2,
    customExpenses: [],
    customTabs: [],
    notes: "Good equity position. Consider IO refinance at next review.",
  },
  {
    id: "belmont",
    nickname: "Belmont",
    address: "8 Regent Street, Belmont VIC",
    suburb: "Belmont",
    state: "VIC",
    postcode: "3216",
    assetClass: "Residential",
    propertyType: "House",
    purchaseDate: "2023-03-06",
    purchasePrice: 590000,
    buyCosts: 31500,
    legalBuyCosts: 2500,
    currentValue: 625000,
    weeklyRent: 480,
    loanBalance: 531000,
    structureId: "chen-trust",
    owner: "Chen Family Trust",
    lender: "Macquarie",
    loanAccountNumber: "MAC-2241",
    offsetAccount: "",
    loanStartDate: "2023-03-04",
    loanTermYears: 30,
    ioTermYears: 0,
    rate: 6.64,
    repaymentType: "P&I",
    rateType: "Variable",
    monthlyExpenses: 720,
    annualRates: 2100,
    annualInsurance: 1500,
    annualStrata: 0,
    annualLandTax: 700,
    annualMaintenance: 1100,
    annualPropertyManagement: 1997,
    personalTopUp: 620,
    estimatedSellingCostsPct: 2.3,
    customExpenses: [],
    customTabs: [],
    notes: "Negatively geared. Monitor trust loss carry-forward position.",
  },
  {
    id: "port-melbourne",
    nickname: "Port Melbourne",
    address: "12/45 Salmon Street, Port Melbourne VIC",
    suburb: "Port Melbourne",
    state: "VIC",
    postcode: "3207",
    assetClass: "Commercial",
    propertyType: "Office",
    purchaseDate: "2022-09-19",
    purchasePrice: 540000,
    buyCosts: 28500,
    legalBuyCosts: 2800,
    currentValue: 650000,
    weeklyRent: 0,
    loanBalance: 324000,
    structureId: "chen-trust",
    owner: "Chen Family Trust",
    lender: "ANZ",
    loanAccountNumber: "ANZ-5502",
    offsetAccount: "",
    loanStartDate: "2022-09-19",
    loanTermYears: 25,
    ioTermYears: 5,
    rate: 7.05,
    repaymentType: "IO",
    rateType: "Variable",
    monthlyExpenses: 0,
    annualRates: 4200,
    annualInsurance: 0,
    annualStrata: 5800,
    annualLandTax: 0,
    annualMaintenance: 0,
    annualPropertyManagement: 4160,
    personalTopUp: 0,
    estimatedSellingCostsPct: 2.5,
    customExpenses: [],
    customTabs: [],
    notes: "IO loan expires 2027-09-19. Lease expires 2026-09-30 — action renewal.",
    leaseStartDate: "2023-10-01",
    leaseEndDate: "2026-09-30",
    rentEscalationType: "Fixed",
    rentEscalationRate: 3.0,
    managementFeePercent: 8,
    incomeRows: [
      { id: "inc-pm-1", category: "Rental income", amount: 52000, frequency: "yearly" },
    ],
    expenseRows: [
      { id: "exp-pm-1", category: "Management fee",          amount: 4160, frequency: "yearly" },
      { id: "exp-pm-2", category: "Body corporate (admin)",  amount: 4200, frequency: "yearly" },
      { id: "exp-pm-3", category: "Body corporate (sinking)",amount: 1600, frequency: "yearly" },
      { id: "exp-pm-4", category: "Council rates",           amount: 4200, frequency: "yearly" },
      { id: "exp-pm-5", category: "Building insurance",      amount: 2200, frequency: "yearly" },
      { id: "exp-pm-6", category: "Accounting fees",         amount: 800,  frequency: "yearly" },
    ],
  },
];

// ─── Budget seed data ─────────────────────────────────────────────────────────

const seedIncomeRows: IncomeRow[] = [
  { id: "ir-1", label: "Take home pay",               amount: 3390, frequency: "F" },
  { id: "ir-2", label: "Bonus / overtime",            amount: 0,    frequency: "F" },
  { id: "ir-3", label: "Rental income (after costs)", amount: 0,    frequency: "W" },
  { id: "ir-4", label: "Dividends",                   amount: 0,    frequency: "Y" },
];

const seedGroups: BudgetGroup[] = [
  { id: "g-1",  name: "Banking & Finance",    sortOrder: 1  },
  { id: "g-2",  name: "Transport",            sortOrder: 2  },
  { id: "g-3",  name: "Household Utilities",  sortOrder: 3  },
  { id: "g-4",  name: "Health & Beauty",      sortOrder: 4  },
  { id: "g-5",  name: "Home",                 sortOrder: 5  },
  { id: "g-6",  name: "Pets",                 sortOrder: 6  },
  { id: "g-7",  name: "Work & Study",         sortOrder: 7  },
  { id: "g-8",  name: "Groceries",            sortOrder: 8  },
  { id: "g-9",  name: "Kids",                 sortOrder: 9  },
  { id: "g-10", name: "Insurances",           sortOrder: 10 },
  { id: "g-11", name: "Investment Property",  sortOrder: 11 },
  { id: "g-12", name: "Miscellaneous",        sortOrder: 12 },
];

const seedBuckets: BudgetBucket[] = [
  { id: "bucket-invest", name: "Investments", type: "investment", description: "Monthly property top-up + savings", monthlyAmount: 2500 },
  { id: "bucket-smile",  name: "Smile",       type: "lifestyle",  description: "Holidays + experiences",           monthlyAmount: 300  },
];

export const budgetSettings: BudgetSettings = {
  incomeRows: seedIncomeRows,
  groups: seedGroups,
  buckets: seedBuckets,
  spareIncomeAvailableForInvesting: null,
};

export const budgetCategories: BudgetCategory[] = [
  // Banking & Finance (g-1)
  { id: "bf-1", category: "Loan Repayment",          budget: 0,      actual: 0, frequency: "F", groupId: "g-1" },
  { id: "bf-2", category: "Credit Card Min",          budget: 0,      actual: 0, frequency: "W", groupId: "g-1" },
  { id: "bf-3", category: "Personal Loan Repayment",  budget: 0,      actual: 0, frequency: "W", groupId: "g-1" },
  { id: "bf-4", category: "Tax / Accountancy fees",   budget: 1500,   actual: 0, frequency: "Y", groupId: "g-1" },
  { id: "bf-5", category: "Card Annual fees",         budget: 395,    actual: 0, frequency: "Y", groupId: "g-1" },
  { id: "bf-6", category: "Other Banking & Finance",  budget: 0,      actual: 0, frequency: "W", groupId: "g-1" },
  // Transport (g-2)
  { id: "tr-1", category: "Vehicle Registration",     budget: 800,    actual: 0, frequency: "Y", groupId: "g-2" },
  { id: "tr-2", category: "Vehicle Maintenance",      budget: 1000,   actual: 0, frequency: "Y", groupId: "g-2" },
  { id: "tr-3", category: "Roadside Assistance",      budget: 120,    actual: 0, frequency: "Y", groupId: "g-2" },
  { id: "tr-4", category: "Fuel",                     budget: 70,     actual: 0, frequency: "W", groupId: "g-2" },
  { id: "tr-5", category: "Parking & Tolls",          budget: 50,     actual: 0, frequency: "M", groupId: "g-2" },
  { id: "tr-6", category: "Public Transport",         budget: 0,      actual: 0, frequency: "W", groupId: "g-2" },
  { id: "tr-7", category: "Taxi & Rideshare",         budget: 20,     actual: 0, frequency: "F", groupId: "g-2" },
  { id: "tr-8", category: "Other Transport",          budget: 0,      actual: 0, frequency: "W", groupId: "g-2" },
  // Household Utilities (g-3)
  { id: "hu-1", category: "Electricity & Gas",        budget: 2200,   actual: 0, frequency: "Y", groupId: "g-3" },
  { id: "hu-2", category: "Internet",                 budget: 79,     actual: 0, frequency: "M", groupId: "g-3" },
  { id: "hu-3", category: "Phone & Mobiles",          budget: 55,     actual: 0, frequency: "M", groupId: "g-3" },
  { id: "hu-4", category: "Streaming Services",       budget: 35,     actual: 0, frequency: "M", groupId: "g-3" },
  { id: "hu-5", category: "Water",                    budget: 0,      actual: 0, frequency: "Q", groupId: "g-3" },
  { id: "hu-6", category: "Cleaning & Other",         budget: 0,      actual: 0, frequency: "F", groupId: "g-3" },
  // Health & Beauty (g-4)
  { id: "hb-1", category: "Hair & Beauty",            budget: 60,     actual: 0, frequency: "M", groupId: "g-4" },
  { id: "hb-2", category: "Gyms & Fitness",           budget: 600,    actual: 0, frequency: "Y", groupId: "g-4" },
  { id: "hb-3", category: "Doctors",                  budget: 50,     actual: 0, frequency: "M", groupId: "g-4" },
  { id: "hb-4", category: "Dentist",                  budget: 300,    actual: 0, frequency: "Y", groupId: "g-4" },
  { id: "hb-5", category: "Medicines & Pharmacy",     budget: 20,     actual: 0, frequency: "M", groupId: "g-4" },
  { id: "hb-6", category: "Other Health & Beauty",    budget: 0,      actual: 0, frequency: "Y", groupId: "g-4" },
  // Home (g-5)
  { id: "ho-1", category: "Rent",                     budget: 460,    actual: 0, frequency: "W", groupId: "g-5" },
  { id: "ho-2", category: "Mortgage Repayments",      budget: 0,      actual: 0, frequency: "M", groupId: "g-5" },
  { id: "ho-3", category: "Body Corp Fees",           budget: 0,      actual: 0, frequency: "Q", groupId: "g-5" },
  { id: "ho-4", category: "Rates",                    budget: 0,      actual: 0, frequency: "Q", groupId: "g-5" },
  { id: "ho-5", category: "Service & Trades",         budget: 0,      actual: 0, frequency: "Q", groupId: "g-5" },
  { id: "ho-6", category: "Other Home",               budget: 0,      actual: 0, frequency: "W", groupId: "g-5" },
  // Pets (g-6)
  { id: "pe-1", category: "Grooming & Services",      budget: 0,      actual: 0, frequency: "W", groupId: "g-6" },
  { id: "pe-2", category: "Vets",                     budget: 0,      actual: 0, frequency: "Y", groupId: "g-6" },
  { id: "pe-3", category: "Pet Food & Supplies",      budget: 0,      actual: 0, frequency: "Y", groupId: "g-6" },
  { id: "pe-4", category: "Pet Insurance",            budget: 0,      actual: 0, frequency: "M", groupId: "g-6" },
  { id: "pe-5", category: "Other Pets",               budget: 0,      actual: 0, frequency: "W", groupId: "g-6" },
  // Work & Study (g-7)
  { id: "ws-1", category: "Books & Stationery",       budget: 0,      actual: 0, frequency: "W", groupId: "g-7" },
  { id: "ws-2", category: "Course Fees",              budget: 0,      actual: 0, frequency: "W", groupId: "g-7" },
  { id: "ws-3", category: "Uniforms & Equipment",     budget: 0,      actual: 0, frequency: "Y", groupId: "g-7" },
  { id: "ws-4", category: "Subscriptions",            budget: 200,    actual: 0, frequency: "Y", groupId: "g-7" },
  { id: "ws-5", category: "Other Work & Study",       budget: 0,      actual: 0, frequency: "Y", groupId: "g-7" },
  // Groceries (g-8)
  { id: "gr-1", category: "Supermarkets",             budget: 180,    actual: 0, frequency: "W", groupId: "g-8" },
  { id: "gr-2", category: "Convenience Stores",       budget: 0,      actual: 0, frequency: "F", groupId: "g-8" },
  { id: "gr-3", category: "Liquor Stores",            budget: 40,     actual: 0, frequency: "W", groupId: "g-8" },
  { id: "gr-4", category: "Other Groceries",          budget: 0,      actual: 0, frequency: "F", groupId: "g-8" },
  // Kids (g-9)
  { id: "ki-1", category: "Activities & Entertainment",budget: 0,     actual: 0, frequency: "F", groupId: "g-9" },
  { id: "ki-2", category: "Childcare",                budget: 0,      actual: 0, frequency: "F", groupId: "g-9" },
  { id: "ki-3", category: "School Fees",              budget: 0,      actual: 0, frequency: "W", groupId: "g-9" },
  { id: "ki-4", category: "School Needs & Uniforms",  budget: 0,      actual: 0, frequency: "W", groupId: "g-9" },
  { id: "ki-5", category: "Medical",                  budget: 0,      actual: 0, frequency: "Y", groupId: "g-9" },
  { id: "ki-6", category: "Other Kids",               budget: 0,      actual: 0, frequency: "Y", groupId: "g-9" },
  // Insurances (g-10)
  { id: "in-1", category: "Health Insurance",         budget: 145.56, actual: 0, frequency: "M", groupId: "g-10" },
  { id: "in-2", category: "Personal & Life Insurance",budget: 0,      actual: 0, frequency: "W", groupId: "g-10" },
  { id: "in-3", category: "Car & Bike Insurance",     budget: 1800,   actual: 0, frequency: "Y", groupId: "g-10" },
  { id: "in-4", category: "Home & Contents Insurance",budget: 350,    actual: 0, frequency: "Y", groupId: "g-10" },
  { id: "in-5", category: "Other Insurance",          budget: 0,      actual: 0, frequency: "W", groupId: "g-10" },
  // Investment Property (g-11)
  { id: "ip-1", category: "Landlord Insurance",       budget: 3800,   actual: 0, frequency: "Y", groupId: "g-11" },
  { id: "ip-2", category: "Annual Package Fee",        budget: 0,      actual: 0, frequency: "W", groupId: "g-11" },
  { id: "ip-3", category: "Body Corp Fees",           budget: 0,      actual: 0, frequency: "Q", groupId: "g-11" },
  { id: "ip-4", category: "Rates",                    budget: 0,      actual: 0, frequency: "Q", groupId: "g-11" },
  { id: "ip-5", category: "Other Investment Property",budget: 0,      actual: 0, frequency: "W", groupId: "g-11" },
  // Miscellaneous (g-12)
  { id: "mi-1", category: "Charity",                  budget: 0,      actual: 0, frequency: "W", groupId: "g-12" },
  { id: "mi-2", category: "Christmas",                budget: 500,    actual: 0, frequency: "Y", groupId: "g-12" },
  { id: "mi-3", category: "Gifts",                    budget: 400,    actual: 0, frequency: "Y", groupId: "g-12" },
  { id: "mi-4", category: "Clothing & Shoes",         budget: 1200,   actual: 0, frequency: "Y", groupId: "g-12" },
  { id: "mi-5", category: "Other Miscellaneous",      budget: 0,      actual: 0, frequency: "W", groupId: "g-12" },
];
